import React, { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, TrendingDown, Search, MapPin, Bot, Play, Clock,
  ChevronDown, ChevronRight, ArrowUp, ArrowDown, Minus, Filter,
  RefreshCw, Zap, AlertTriangle, CheckCircle, XCircle, BarChart3,
  Target, Eye, Layers, Plus, Trash2, Pause, ToggleLeft, ToggleRight,
  Edit3, Globe, List, Copy, Download,
} from "lucide-react";
import api from "../api/client";

// ─── helpers ─────────────────────────────────────────────────────
const badge = (pos) => {
  if (pos === null || pos === undefined) return "bg-gray-700 text-gray-400";
  if (pos <= 3) return "bg-green-900/60 text-green-300 border border-green-700/40";
  if (pos <= 10) return "bg-blue-900/60 text-blue-300 border border-blue-700/40";
  if (pos <= 20) return "bg-yellow-900/60 text-yellow-300 border border-yellow-700/40";
  return "bg-gray-800 text-gray-400 border border-gray-700/40";
};

const changeIcon = (change) => {
  if (!change || change === 0) return <Minus size={14} className="text-gray-500" />;
  if (change > 0) return <ArrowUp size={14} className="text-green-400" />;
  return <ArrowDown size={14} className="text-red-400" />;
};

const changeColor = (change) => {
  if (!change || change === 0) return "text-gray-500";
  return change > 0 ? "text-green-400" : "text-red-400";
};

const scoreColor = (score) => {
  if (score >= 70) return "text-green-400 border-green-500/50 bg-green-900/20";
  if (score >= 50) return "text-yellow-400 border-yellow-500/50 bg-yellow-900/20";
  if (score >= 30) return "text-orange-400 border-orange-500/50 bg-orange-900/20";
  return "text-red-400 border-red-500/50 bg-red-900/20";
};

const priorityBadge = (p) => {
  const map = {
    critical: "bg-red-900/60 text-red-300 border-red-700/40",
    high: "bg-orange-900/60 text-orange-300 border-orange-700/40",
    medium: "bg-yellow-900/60 text-yellow-300 border-yellow-700/40",
    low: "bg-green-900/60 text-green-300 border-green-700/40",
  };
  return map[p] || map.medium;
};

const statusBadge = (s) => {
  const map = {
    pending: "bg-gray-800 text-gray-400",
    running: "bg-blue-900/60 text-blue-300",
    completed: "bg-green-900/60 text-green-300",
    failed: "bg-red-900/60 text-red-300",
    partial: "bg-yellow-900/60 text-yellow-300",
    generated: "bg-blue-900/60 text-blue-300",
    reviewed: "bg-purple-900/60 text-purple-300",
    actioned: "bg-green-900/60 text-green-300",
    dismissed: "bg-gray-800 text-gray-500",
  };
  return map[s] || map.pending;
};

// Keyword modifier templates
const KEYWORD_MODIFIERS = [
  { label: "Near Me", suffix: " near me" },
  { label: "Best", prefix: "best " },
  { label: "Affordable", prefix: "affordable " },
  { label: "Top", prefix: "top " },
  { label: "Professional", prefix: "professional " },
  { label: "Cost", suffix: " cost" },
  { label: "Services", suffix: " services" },
  { label: "Company", suffix: " company" },
  { label: "Reviews", suffix: " reviews" },
  { label: "Prices", suffix: " prices" },
];

const COUNTRIES = [
  "United States", "United Kingdom", "Canada", "Australia", "Germany",
  "France", "Italy", "Spain", "Netherlands", "Albania", "Brazil",
  "Mexico", "India", "Japan", "South Korea", "Singapore", "Indonesia",
  "Philippines", "Turkey", "Greece", "Portugal", "Switzerland",
  "Sweden", "Norway", "Denmark", "Finland", "Ireland", "New Zealand",
  "South Africa", "United Arab Emirates", "Saudi Arabia", "Poland",
  "Czech Republic", "Romania", "Hungary", "Croatia", "Serbia",
  "Bosnia and Herzegovina", "Montenegro", "North Macedonia", "Kosovo",
];

// ─── stat card ───────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, sub, color = "text-copper" }) {
  // ── Rankings fetchers ──
  const fetchTargetKeywords = useCallback(async () => {
    if (!selectedSite?.id) return;
    setRankingsLoading(true);
    try {
      const params = { site_id: selectedSite.id };
      if (intentFilter) params.intent = intentFilter;
      const { data } = await api.get("/rank-tracker/target-keywords/", { params });
      setTargetKeywords(data.keywords || []);
    } catch (e) {
      console.error("Failed to fetch target keywords", e);
    } finally {
      setRankingsLoading(false);
    }
  }, [selectedSite?.id, intentFilter]);

  const fetchCompetitorDomains = useCallback(async () => {
    if (!selectedSite?.id) return;
    try {
      const { data } = await api.get("/rank-tracker/competitor-domains/", {
        params: { site_id: selectedSite.id, hide_directories: hideDirectories },
      });
      setCompetitorDomains(data.competitors || []);
    } catch (e) {
      console.error("Failed to fetch competitors", e);
    }
  }, [selectedSite?.id, hideDirectories]);

  useEffect(() => {
    if (tab === "Rankings") {
      fetchTargetKeywords();
      fetchCompetitorDomains();
    }
  }, [tab, fetchTargetKeywords, fetchCompetitorDomains]);

  const handleAddKeyword = async () => {
    if (!newKwPhrase.trim()) return;
    try {
      await api.post("/rank-tracker/target-keywords/create/", {
        site_id: selectedSite.id,
        phrase: newKwPhrase.trim(),
        location: newKwLocation.trim(),
        intent: "commercial",
        source: "manual",
      });
      setNewKwPhrase("");
      fetchTargetKeywords();
    } catch (e) {
      console.error("Failed to add keyword", e);
    }
  };

  const handleToggleKeyword = async (id, active) => {
    try {
      await api.patch(`/rank-tracker/target-keywords/${id}/update/`, { active: !active });
      fetchTargetKeywords();
    } catch (e) {
      console.error("Failed to toggle keyword", e);
    }
  };

  const handleDeleteKeyword = async (id) => {
    if (!window.confirm("Delete this keyword and its history?")) return;
    try {
      await api.delete(`/rank-tracker/target-keywords/${id}/delete/`);
      fetchTargetKeywords();
    } catch (e) {
      console.error("Failed to delete keyword", e);
    }
  };

  const handlePinCompetitor = async (id) => {
    try {
      await api.post(`/rank-tracker/competitor-domains/${id}/pin/`);
      fetchCompetitorDomains();
    } catch (e) {
      console.error("Failed to pin competitor", e);
    }
  };

  const handleAddCompetitor = async () => {
    if (!newCompDomain.trim()) return;
    try {
      await api.post("/rank-tracker/competitor-domains/add/", {
        site_id: selectedSite.id,
        domain: newCompDomain.trim(),
      });
      setNewCompDomain("");
      fetchCompetitorDomains();
    } catch (e) {
      console.error("Failed to add competitor", e);
    }
  };

  const handleRunRankCheck = async () => {
    try {
      await api.post("/rank-tracker/run-check/", { site_id: selectedSite.id });
      alert("Rank check started! Results will appear after completion.");
    } catch (e) {
      console.error("Failed to start rank check", e);
    }
  };


  return (
    <div className="bg-dark-200 border border-dark-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
        {Icon && <Icon size={14} className="text-gray-600" />}
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

// ─── TABS ────────────────────────────────────────────────────────
const TABS = ["Overview", "Rankings", "Results", "AI Analysis", "Jobs", "Keywords", "Locations"];

// ─── main component ─────────────────────────────────────────────
export default function RankTracker({ selectedSite }) {
  const [tab, setTab] = useState("Overview");
  const [dash, setDash] = useState(null);
  const [results, setResults] = useState([]);
  const [resultsCount, setResultsCount] = useState(0);
  const [analyses, setAnalyses] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);
  const [apiUsage, setApiUsage] = useState(null);

  // Rankings (Phase-2)
  const [targetKeywords, setTargetKeywords] = useState([]);
  const [competitorDomains, setCompetitorDomains] = useState([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [hideDirectories, setHideDirectories] = useState(true);
  const [intentFilter, setIntentFilter] = useState("");
  const [newKwPhrase, setNewKwPhrase] = useState("");
  const [newKwLocation, setNewKwLocation] = useState("New York, New York, United States");
  const [newCompDomain, setNewCompDomain] = useState("");

  // Filters
  const [filterGroup, setFilterGroup] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterKeyword, setFilterKeyword] = useState("");
  const [filterPage1, setFilterPage1] = useState(false);
  const [filterNotFound, setFilterNotFound] = useState(false);
  const [resultsPage, setResultsPage] = useState(1);

  // Job creation
  const [showJobForm, setShowJobForm] = useState(false);
  const [jobName, setJobName] = useState("");
  const [jobFreq, setJobFreq] = useState("manual");
  const [jobAI, setJobAI] = useState(true);
  const [jobGroupIds, setJobGroupIds] = useState([]);
  const [jobLocationIds, setJobLocationIds] = useState([]);

  const siteId = selectedSite?.id;

  // Reset state when site changes
  useEffect(() => {
    setDash(null);
    setResults([]);
    setResultsCount(0);
    setAnalyses([]);
    setJobs([]);
    setGroups([]);
    setKeywords([]);
    setLocations([]);
    setShowJobForm(false);
    setTab("Overview");
    setApiUsage(null);
  }, [siteId]);

  // ── fetchers ───────────────────────────────────────────────────
  const fetchDashboard = useCallback(async () => {
    if (!siteId) return;
    try {
      const r = await api.get(`/rank-tracker/dashboard/?site_id=${siteId}`);
      setDash(r.data);
    } catch { setDash(null); }
  }, [siteId]);

  const fetchResults = useCallback(async () => {
    if (!siteId) return;
    const params = new URLSearchParams({ site_id: siteId, page: resultsPage, page_size: 50 });
    if (filterGroup) params.set("group", filterGroup);
    if (filterCity) params.set("city", filterCity);
    if (filterKeyword) params.set("keyword", filterKeyword);
    if (filterPage1) params.set("page_1_only", "true");
    if (filterNotFound) params.set("not_found_only", "true");
    try {
      const r = await api.get(`/rank-tracker/results/?${params}`);
      setResults(r.data.results || []);
      setResultsCount(r.data.count || 0);
    } catch { setResults([]); }
  }, [siteId, filterGroup, filterCity, filterKeyword, filterPage1, filterNotFound, resultsPage]);

  const fetchAnalyses = useCallback(async () => {
    if (!siteId) return;
    try {
      const r = await api.get(`/rank-tracker/analyses/?site_id=${siteId}`);
      setAnalyses(r.data.analyses || []);
    } catch { setAnalyses([]); }
  }, [siteId]);

  const fetchJobs = useCallback(async () => {
    if (!siteId) return;
    try {
      const r = await api.get(`/rank-tracker/jobs/?site_id=${siteId}`);
      setJobs(r.data.jobs || []);
    } catch { setJobs([]); }
  }, [siteId]);

  const fetchKeywordsLocations = useCallback(async () => {
    if (!siteId) return;
    try {
      const [kw, loc] = await Promise.all([
        api.get(`/rank-tracker/keywords/?site_id=${siteId}`),
        api.get(`/rank-tracker/locations/?site_id=${siteId}`),
      ]);
      setGroups(kw.data.groups || []);
      setKeywords(kw.data.keywords || []);
      setLocations(loc.data.locations || []);
    } catch {}
  }, [siteId]);

  const fetchApiUsage = useCallback(async () => {
    if (!siteId) return;
    try {
      const r = await api.get(`/rank-tracker/api-usage/?site_id=${siteId}`);
      setApiUsage(r.data);
    } catch { setApiUsage(null); }
  }, [siteId]);

  // ── initial load ───────────────────────────────────────────────
  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    Promise.all([fetchDashboard(), fetchKeywordsLocations(), fetchApiUsage()])
      .finally(() => setLoading(false));
  }, [siteId, fetchDashboard, fetchKeywordsLocations, fetchApiUsage]);

  useEffect(() => {
    if (tab === "Results") fetchResults();
    if (tab === "AI Analysis") fetchAnalyses();
    if (tab === "Jobs") fetchJobs();
  }, [tab, fetchResults, fetchAnalyses, fetchJobs]);

  // ── actions ────────────────────────────────────────────────────
  const handleCreateJob = async () => {
    const body = {
      site_id: siteId,
      name: jobName || `Rank Check ${new Date().toLocaleDateString()}`,
      frequency: jobFreq,
      run_ai_analysis: jobAI,
      run_immediately: true,
      keyword_group_ids: jobGroupIds,
      location_ids: jobLocationIds,
    };
    try {
      await api.post("/rank-tracker/create_job/", body);
      setShowJobForm(false);
      setJobName("");
      fetchJobs();
      fetchApiUsage();
      alert("Rank check job started!");
    } catch (e) {
      alert("Failed: " + (e.response?.data?.error || e.message));
    }
  };

  const handleStatusUpdate = async (analysisId, newStatus) => {
    try {
      await api.post(`/rank-tracker/analyses/${analysisId}/status/`, { status: newStatus });
      fetchAnalyses();
    } catch {}
  };

  // ── setup required ─────────────────────────────────────────────
  if (!siteId) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Select a website to view rank tracking data.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={24} className="text-copper animate-spin" />
      </div>
    );
  }

  const setupRequired = dash?.setup_required;

  // ── render ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Target size={24} className="text-copper" />
            Rank Tracker
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track keyword rankings across locations with AI oversight
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* API Usage Badge */}
          {apiUsage && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${
              apiUsage.calls_remaining < 500 ? "border-red-700/40 bg-red-900/20 text-red-300" :
              apiUsage.calls_remaining < 1500 ? "border-yellow-700/40 bg-yellow-900/20 text-yellow-300" :
              "border-dark-50 bg-dark-200 text-gray-400"
            }`}>
              <Zap size={12} />
              {apiUsage.calls_remaining.toLocaleString()} / {apiUsage.monthly_budget.toLocaleString()} credits
            </div>
          )}
          {!setupRequired && (
            <button
              onClick={() => setShowJobForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium transition"
            >
              <Play size={16} /> Run Rank Check
            </button>
          )}
        </div>
      </div>

      {/* Setup prompt */}
      {setupRequired && (
        <SetupPanel siteId={siteId} domain={selectedSite?.domain} onComplete={() => {
          fetchDashboard();
          fetchKeywordsLocations();
          fetchApiUsage();
        }} />
      )}

      {/* Tabs */}
      {!setupRequired && (
        <>
          <div className="flex gap-1 bg-dark-300 rounded-lg p-1 w-fit">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  tab === t
                    ? "bg-dark-100 text-white shadow"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {t}
                {t === "Keywords" && keywords.length > 0 && (
                  <span className="ml-1.5 text-xs text-gray-600">({keywords.length})</span>
                )}
                {t === "Locations" && locations.length > 0 && (
                  <span className="ml-1.5 text-xs text-gray-600">({locations.length})</span>
                )}
              </button>
            ))}
          </div>

          {tab === "Overview" && <OverviewTab dash={dash} fetchDashboard={fetchDashboard} />}
          {tab === "Rankings" && (
            <div className="space-y-6">
              {/* Controls bar */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleRunRankCheck}
                  className="flex items-center gap-2 bg-copper/20 text-copper px-4 py-2 rounded-lg hover:bg-copper/30 text-sm"
                >
                  <Play size={14} /> Run Check Now
                </button>
                <select
                  value={intentFilter}
                  onChange={(e) => setIntentFilter(e.target.value)}
                  className="bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300"
                >
                  <option value="">All intents</option>
                  <option value="commercial">Commercial</option>
                  <option value="informational">Informational</option>
                </select>
                <button
                  onClick={fetchTargetKeywords}
                  className="p-2 text-gray-400 hover:text-gray-200"
                >
                  <RefreshCw size={14} />
                </button>
              </div>

              {/* Stats row */}
              {(() => {
                const total = targetKeywords.length;
                const ranked = targetKeywords.filter(k => k.current_position != null).length;
                const page1 = targetKeywords.filter(k => k.current_position != null && k.current_position <= 10).length;
                const notFound = targetKeywords.filter(k => k.current_position == null && k.last_checked).length;
                const commGaps = targetKeywords.filter(k => k.intent === "commercial" && k.current_position == null).length;
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <StatCard label="Tracked" value={total} icon={Target} />
                    <StatCard label="Ranking" value={ranked} icon={Eye} color="text-blue-400" />
                    <StatCard label="Page 1" value={page1} icon={CheckCircle} color="text-green-400" />
                    <StatCard label="Not Found" value={notFound} icon={XCircle} color="text-gray-400" />
                    <StatCard label="Comm. Gaps" value={commGaps} icon={AlertTriangle} color="text-red-400" />
                  </div>
                );
              })()}

              {/* Keywords table */}
              <div className="bg-dark-200 border border-dark-50 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-dark-50 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-300">Target Keywords</h3>
                  <div className="flex items-center gap-2">
                    <input
                      value={newKwPhrase}
                      onChange={(e) => setNewKwPhrase(e.target.value)}
                      placeholder="Add keyword..."
                      className="bg-dark-300 border border-dark-50 rounded px-2 py-1 text-xs text-gray-300 w-40"
                      onKeyDown={(e) => e.key === "Enter" && handleAddKeyword()}
                    />
                    <input
                      value={newKwLocation}
                      onChange={(e) => setNewKwLocation(e.target.value)}
                      placeholder="Location"
                      className="bg-dark-300 border border-dark-50 rounded px-2 py-1 text-xs text-gray-300 w-48"
                    />
                    <button
                      onClick={handleAddKeyword}
                      className="p-1 text-copper hover:text-copper/80"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs uppercase border-b border-dark-50">
                        <th className="text-left px-4 py-2">Keyword</th>
                        <th className="text-left px-3 py-2">Location</th>
                        <th className="text-center px-3 py-2">Position</th>
                        <th className="text-center px-3 py-2">7d</th>
                        <th className="text-center px-3 py-2">30d</th>
                        <th className="text-left px-3 py-2">URL</th>
                        <th className="text-center px-3 py-2">Intent</th>
                        <th className="text-center px-3 py-2">Source</th>
                        <th className="text-center px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingsLoading ? (
                        <tr><td colSpan={9} className="text-center py-8 text-gray-500">Loading...</td></tr>
                      ) : targetKeywords.length === 0 ? (
                        <tr><td colSpan={9} className="text-center py-8 text-gray-500">No keywords tracked yet. Run the seed command or add keywords above.</td></tr>
                      ) : (
                        targetKeywords.map((kw) => {
                          const isCommGap = kw.intent === "commercial" && kw.current_position == null;
                          const isPage1 = kw.current_position != null && kw.current_position <= 10;
                          const rowClass = isCommGap
                            ? "border-b border-dark-50 bg-red-900/10"
                            : isPage1
                              ? "border-b border-dark-50 bg-green-900/10"
                              : "border-b border-dark-50 hover:bg-dark-300/50";
                          return (
                            <tr key={kw.id} className={rowClass}>
                              <td className={`px-4 py-2 font-medium ${kw.active ? "text-gray-200" : "text-gray-600 line-through"}`}>
                                {kw.phrase}
                                {isCommGap && <span className="ml-2 text-[10px] text-red-400 font-semibold">GAP</span>}
                                {isPage1 && <span className="ml-2 text-[10px] text-green-400 font-semibold">P1</span>}
                              </td>
                              <td className="px-3 py-2 text-gray-400 text-xs">{kw.location?.split(",")[0]}</td>
                              <td className="px-3 py-2 text-center">
                                {kw.current_position != null ? (
                                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono ${badge(kw.current_position)}`}>
                                    #{kw.current_position}
                                  </span>
                                ) : kw.last_checked ? (
                                  <span className="text-gray-600 text-xs">—</span>
                                ) : (
                                  <span className="text-gray-700 text-xs">pending</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {kw.delta_7d != null ? (
                                  <span className={`flex items-center justify-center gap-0.5 text-xs ${changeColor(kw.delta_7d)}`}>
                                    {changeIcon(kw.delta_7d)}
                                    {Math.abs(kw.delta_7d)}
                                  </span>
                                ) : <span className="text-gray-700">—</span>}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {kw.delta_30d != null ? (
                                  <span className={`flex items-center justify-center gap-0.5 text-xs ${changeColor(kw.delta_30d)}`}>
                                    {changeIcon(kw.delta_30d)}
                                    {Math.abs(kw.delta_30d)}
                                  </span>
                                ) : <span className="text-gray-700">—</span>}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-500 max-w-[200px] truncate">
                                {kw.ranking_url ? (
                                  <a href={kw.ranking_url} target="_blank" rel="noopener noreferrer" className="hover:text-copper">
                                    {kw.ranking_url.replace(/https?:\/\//, "").slice(0, 50)}
                                  </a>
                                ) : "—"}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${kw.intent === "commercial" ? "bg-blue-900/40 text-blue-300" : "bg-gray-800 text-gray-500"}`}>
                                  {kw.intent}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className="text-[10px] text-gray-500">{kw.source}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => handleToggleKeyword(kw.id, kw.active)}
                                    className="p-1 text-gray-500 hover:text-gray-300"
                                    title={kw.active ? "Pause" : "Activate"}
                                  >
                                    {kw.active ? <ToggleRight size={14} className="text-green-400" /> : <ToggleLeft size={14} />}
                                  </button>
                                  <button
                                    onClick={() => handleDeleteKeyword(kw.id)}
                                    className="p-1 text-gray-600 hover:text-red-400"
                                    title="Delete"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Competitors table */}
              <div className="bg-dark-200 border border-dark-50 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-dark-50 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-300">Competitors</h3>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={hideDirectories}
                        onChange={(e) => setHideDirectories(e.target.checked)}
                        className="rounded"
                      />
                      Hide directories
                    </label>
                    <input
                      value={newCompDomain}
                      onChange={(e) => setNewCompDomain(e.target.value)}
                      placeholder="Add competitor domain..."
                      className="bg-dark-300 border border-dark-50 rounded px-2 py-1 text-xs text-gray-300 w-44"
                      onKeyDown={(e) => e.key === "Enter" && handleAddCompetitor()}
                    />
                    <button onClick={handleAddCompetitor} className="p-1 text-copper hover:text-copper/80">
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs uppercase border-b border-dark-50">
                        <th className="text-left px-4 py-2">Domain</th>
                        <th className="text-center px-3 py-2">Times Seen</th>
                        <th className="text-center px-3 py-2">Avg Position</th>
                        <th className="text-center px-3 py-2">Type</th>
                        <th className="text-center px-3 py-2">Last Seen</th>
                        <th className="text-center px-3 py-2">Pin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competitorDomains.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-6 text-gray-500">No competitors found yet. Run a rank check first.</td></tr>
                      ) : (
                        competitorDomains.map((c) => (
                          <tr key={c.id} className={`border-b border-dark-50 hover:bg-dark-300/50 ${c.is_pinned ? "bg-copper/5" : ""}`}>
                            <td className="px-4 py-2 font-medium text-gray-200">{c.domain}</td>
                            <td className="px-3 py-2 text-center text-gray-400">{c.times_seen}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono ${badge(c.avg_position)}`}>
                                #{c.avg_position}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.is_directory ? "bg-gray-800 text-gray-500" : "bg-blue-900/40 text-blue-300"}`}>
                                {c.is_directory ? "directory" : "competitor"}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-gray-500">{c.last_seen}</td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => handlePinCompetitor(c.id)}
                                className={`p-1 ${c.is_pinned ? "text-copper" : "text-gray-600 hover:text-gray-300"}`}
                                title={c.is_pinned ? "Unpin" : "Pin"}
                              >
                                <Target size={14} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
                    {tab === "Results" && (
            <ResultsTab
              results={results} count={resultsCount} groups={groups}
              filterGroup={filterGroup} setFilterGroup={setFilterGroup}
              filterCity={filterCity} setFilterCity={setFilterCity}
              filterKeyword={filterKeyword} setFilterKeyword={setFilterKeyword}
              filterPage1={filterPage1} setFilterPage1={setFilterPage1}
              filterNotFound={filterNotFound} setFilterNotFound={setFilterNotFound}
              page={resultsPage} setPage={setResultsPage}
            />
          )}
          {tab === "AI Analysis" && (
            <AnalysisTab
              analyses={analyses} expanded={expandedAnalysis}
              setExpanded={setExpandedAnalysis} onStatusUpdate={handleStatusUpdate}
            />
          )}
          {tab === "Jobs" && <JobsTab jobs={jobs} onRefresh={fetchJobs} />}
          {tab === "Keywords" && (
            <KeywordsManageTab
              siteId={siteId} keywords={keywords} groups={groups}
              onRefresh={fetchKeywordsLocations}
            />
          )}
          {tab === "Locations" && (
            <LocationsManageTab
              siteId={siteId} locations={locations}
              onRefresh={() => { fetchKeywordsLocations(); fetchApiUsage(); }}
            />
          )}
        </>
      )}

      {/* Job creation modal */}
      {showJobForm && (
        <JobModal
          groups={groups} locations={locations} apiUsage={apiUsage}
          jobName={jobName} setJobName={setJobName}
          jobFreq={jobFreq} setJobFreq={setJobFreq}
          jobAI={jobAI} setJobAI={setJobAI}
          jobGroupIds={jobGroupIds} setJobGroupIds={setJobGroupIds}
          jobLocationIds={jobLocationIds} setJobLocationIds={setJobLocationIds}
          onCreate={handleCreateJob} onClose={() => setShowJobForm(false)}
        />
      )}
    </div>
  );
}

// ─── Overview Tab ────────────────────────────────────────────────
function OverviewTab({ dash, fetchDashboard }) {
  if (!dash) return null;

  if (dash.no_results) {
    return (
      <div className="bg-dark-200 border border-dark-50 rounded-xl p-8 text-center">
        <Clock size={48} className="text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">No Results Yet</h3>
        <p className="text-gray-400 text-sm">
          {dash.keywords_count} keywords and {dash.locations_count} locations are configured.
          Run a rank check to see results.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <StatCard label="Tracked" value={dash.total_tracked} icon={Target} />
        <StatCard label="Page 1" value={dash.page_1} icon={CheckCircle} color="text-green-400" />
        <StatCard label="Page 2-3" value={dash.page_2_3} icon={Layers} color="text-blue-400" />
        <StatCard label="Not Found" value={dash.not_found} icon={XCircle} color="text-red-400" />
        <StatCard label="Improved" value={dash.improved} icon={TrendingUp} color="text-green-400" />
        <StatCard label="Declined" value={dash.declined} icon={TrendingDown} color="text-red-400" />
        <StatCard label="Local Pack" value={dash.local_pack} icon={MapPin} color="text-purple-400" />
        <StatCard label="Avg Position" value={dash.avg_position} icon={BarChart3} />
      </div>

      {/* AI Score + Summary */}
      {dash.ai_score !== null && (
        <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-16 h-16 rounded-xl border-2 flex items-center justify-center text-2xl font-bold ${scoreColor(dash.ai_score)}`}>
              {dash.ai_score}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Bot size={16} className="text-copper" />
                <span className="text-sm font-medium text-white">AI Analysis Score</span>
              </div>
              <p className="text-sm text-gray-400">{dash.ai_summary}</p>
            </div>
          </div>
        </div>
      )}

      {/* Top movers + drops */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MoverTable title="Top Movers" icon={TrendingUp} color="text-green-400" data={dash.top_movers} />
        <MoverTable title="Biggest Drops" icon={TrendingDown} color="text-red-400" data={dash.top_drops} />
      </div>

      {dash.last_check && (
        <p className="text-xs text-gray-600 text-right">
          Last check: {new Date(dash.last_check).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function MoverTable({ title, icon: Icon, color, data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="bg-dark-200 border border-dark-50 rounded-xl p-4">
      <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${color}`}>
        <Icon size={16} /> {title}
      </h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-500 text-xs">
            <th className="text-left py-1">Keyword</th>
            <th className="text-left py-1">City</th>
            <th className="text-center py-1">Pos</th>
            <th className="text-center py-1">Change</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={i} className="border-t border-dark-50">
              <td className="py-2 text-white">{r.keyword_text}</td>
              <td className="py-2 text-gray-400">{r.city}</td>
              <td className="py-2 text-center">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono ${badge(r.position)}`}>
                  {r.position || "N/F"}
                </span>
              </td>
              <td className="py-2 text-center">
                <span className={`flex items-center justify-center gap-1 ${changeColor(r.position_change)}`}>
                  {changeIcon(r.position_change)}
                  {Math.abs(r.position_change || 0)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Results Tab ─────────────────────────────────────────────────
function ResultsTab({
  results, count, groups,
  filterGroup, setFilterGroup,
  filterCity, setFilterCity,
  filterKeyword, setFilterKeyword,
  filterPage1, setFilterPage1,
  filterNotFound, setFilterNotFound,
  page, setPage,
}) {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-dark-200 border border-dark-50 rounded-xl p-4">
        <Filter size={16} className="text-gray-500" />
        <select
          value={filterGroup}
          onChange={(e) => { setFilterGroup(e.target.value); setPage(1); }}
          className="bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-1.5"
        >
          <option value="">All Groups</option>
          {groups.map((g) => (
            <option key={g.slug} value={g.slug}>{g.name} ({g.keyword_count})</option>
          ))}
        </select>
        <input
          type="text" placeholder="City..."
          value={filterCity}
          onChange={(e) => { setFilterCity(e.target.value); setPage(1); }}
          className="bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-1.5 w-32"
        />
        <input
          type="text" placeholder="Keyword..."
          value={filterKeyword}
          onChange={(e) => { setFilterKeyword(e.target.value); setPage(1); }}
          className="bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-1.5 w-40"
        />
        <label className="flex items-center gap-1.5 text-sm text-gray-400 cursor-pointer">
          <input
            type="checkbox" checked={filterPage1}
            onChange={(e) => { setFilterPage1(e.target.checked); setPage(1); }}
            className="rounded bg-dark-300 border-dark-50"
          />
          Page 1 only
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-400 cursor-pointer">
          <input
            type="checkbox" checked={filterNotFound}
            onChange={(e) => { setFilterNotFound(e.target.checked); setPage(1); }}
            className="rounded bg-dark-300 border-dark-50"
          />
          Not found
        </label>
        <span className="text-xs text-gray-600 ml-auto">{count} results</span>
      </div>

      {/* Table */}
      <div className="bg-dark-200 border border-dark-50 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-dark-50">
              <th className="text-left px-4 py-3">Keyword</th>
              <th className="text-left px-4 py-3">Group</th>
              <th className="text-left px-4 py-3">City</th>
              <th className="text-center px-4 py-3">Position</th>
              <th className="text-center px-4 py-3">Change</th>
              <th className="text-center px-4 py-3">Local Pack</th>
              <th className="text-left px-4 py-3">URL Found</th>
            </tr>
          </thead>
          <tbody>
            {results.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-500">
                  No results match your filters.
                </td>
              </tr>
            )}
            {results.map((r, i) => (
              <tr key={i} className="border-t border-dark-50 hover:bg-dark-300/50 transition">
                <td className="px-4 py-2.5 text-white font-medium">{r.keyword_text}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{r.group_name}</td>
                <td className="px-4 py-2.5 text-gray-400">{r.city}, {r.state}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`inline-block px-2.5 py-0.5 rounded text-xs font-mono font-bold ${badge(r.position)}`}>
                    {r.position || "N/F"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`flex items-center justify-center gap-1 font-mono ${changeColor(r.position_change)}`}>
                    {changeIcon(r.position_change)}
                    {r.position_change ? Math.abs(r.position_change) : "—"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-center">
                  {r.in_local_pack ? (
                    <span className="inline-block px-2 py-0.5 rounded text-xs bg-purple-900/60 text-purple-300 border border-purple-700/40">
                      #{r.local_pack_position}
                    </span>
                  ) : (
                    <span className="text-gray-600">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-gray-500 text-xs truncate max-w-[200px]">
                  {r.url_found ? (
                    <a href={r.url_found} target="_blank" rel="noopener noreferrer" className="hover:text-copper">
                      {new URL(r.url_found).pathname}
                    </a>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {count > 50 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 bg-dark-200 border border-dark-50 rounded text-sm text-gray-400 hover:text-white disabled:opacity-30"
          >
            Prev
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {Math.ceil(count / 50)}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= Math.ceil(count / 50)}
            className="px-3 py-1.5 bg-dark-200 border border-dark-50 rounded text-sm text-gray-400 hover:text-white disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ─── AI Analysis Tab ─────────────────────────────────────────────
function AnalysisTab({ analyses, expanded, setExpanded, onStatusUpdate }) {
  const [detail, setDetail] = useState(null);

  const loadDetail = async (id) => {
    if (expanded === id) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    try {
      const r = await api.get(`/rank-tracker/analyses/${id}/`);
      setDetail(r.data);
      setExpanded(id);
    } catch {
      setExpanded(id);
    }
  };

  if (analyses.length === 0) {
    return (
      <div className="bg-dark-200 border border-dark-50 rounded-xl p-8 text-center">
        <Bot size={48} className="text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">No AI Analyses Yet</h3>
        <p className="text-gray-400 text-sm">
          Run a rank check with AI analysis enabled to generate insights.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {analyses.map((a) => (
        <div key={a.id} className="bg-dark-200 border border-dark-50 rounded-xl overflow-hidden">
          {/* Header row */}
          <button
            onClick={() => loadDetail(a.id)}
            className="w-full flex items-center gap-4 p-4 text-left hover:bg-dark-300/50 transition"
          >
            <div className={`flex-shrink-0 w-12 h-12 rounded-lg border-2 flex items-center justify-center text-lg font-bold ${scoreColor(a.overall_score)}`}>
              {a.overall_score}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${priorityBadge(a.priority)}`}>
                  {a.priority}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs ${statusBadge(a.status)}`}>
                  {a.status}
                </span>
                <span className="text-xs text-gray-600">
                  {new Date(a.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-sm text-gray-300 truncate">{a.summary}</p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-4 text-xs text-gray-500">
              <span>{a.keywords_ranking} on page 1</span>
              <span className="text-green-400">+{a.keywords_improved}</span>
              <span className="text-red-400">-{a.keywords_declined}</span>
              {expanded === a.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </div>
          </button>

          {/* Expanded detail */}
          {expanded === a.id && detail && (
            <div className="border-t border-dark-50 p-4 space-y-4">
              {/* Actions */}
              <div className="flex gap-2">
                {a.status === "generated" && (
                  <>
                    <button
                      onClick={() => onStatusUpdate(a.id, "reviewed")}
                      className="px-3 py-1.5 bg-purple-900/40 text-purple-300 border border-purple-700/40 rounded text-xs hover:bg-purple-900/60"
                    >
                      Mark Reviewed
                    </button>
                    <button
                      onClick={() => onStatusUpdate(a.id, "dismissed")}
                      className="px-3 py-1.5 bg-dark-300 text-gray-400 border border-dark-50 rounded text-xs hover:text-white"
                    >
                      Dismiss
                    </button>
                  </>
                )}
                {a.status === "reviewed" && (
                  <button
                    onClick={() => onStatusUpdate(a.id, "actioned")}
                    className="px-3 py-1.5 bg-green-900/40 text-green-300 border border-green-700/40 rounded text-xs hover:bg-green-900/60"
                  >
                    Mark Actioned
                  </button>
                )}
              </div>

              {/* Recommendations */}
              {detail.recommendations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-2">Recommendations</h4>
                  <div className="space-y-2">
                    {detail.recommendations.map((rec, i) => (
                      <div key={i} className="bg-dark-300 rounded-lg p-3 text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-1.5 py-0.5 rounded text-xs border ${priorityBadge(rec.priority)}`}>
                            {rec.priority}
                          </span>
                          <span className="text-gray-500 text-xs">{rec.category}</span>
                          {rec.estimated_impact && (
                            <span className="text-xs text-gray-600">Impact: {rec.estimated_impact}</span>
                          )}
                        </div>
                        <p className="text-white font-medium">{rec.action}</p>
                        {rec.keyword && <p className="text-gray-500 text-xs mt-1">Keyword: {rec.keyword}</p>}
                        {rec.location && <p className="text-gray-500 text-xs">Location: {rec.location}</p>}
                        {rec.details && <p className="text-gray-400 text-xs mt-1">{rec.details}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full markdown report */}
              {detail.full_analysis && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-2">Full Report</h4>
                  <div className="bg-dark-300 rounded-lg p-4 text-sm text-gray-300 whitespace-pre-wrap max-h-96 overflow-y-auto font-mono text-xs">
                    {detail.full_analysis}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Jobs Tab ────────────────────────────────────────────────────
function JobsTab({ jobs, onRefresh }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Rank Check Jobs</h3>
        <button onClick={onRefresh} className="text-gray-500 hover:text-white">
          <RefreshCw size={16} />
        </button>
      </div>

      {jobs.length === 0 && (
        <div className="bg-dark-200 border border-dark-50 rounded-xl p-8 text-center text-gray-500">
          No jobs yet. Click "Run Rank Check" to create one.
        </div>
      )}

      {jobs.map((j) => (
        <div key={j.id} className="bg-dark-200 border border-dark-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h4 className="text-sm font-medium text-white">{j.name}</h4>
              <span className={`px-2 py-0.5 rounded text-xs ${statusBadge(j.status)}`}>
                {j.status}
              </span>
              <span className="text-xs text-gray-600">{j.frequency}</span>
            </div>
            <span className="text-xs text-gray-600">
              {new Date(j.created_at).toLocaleString()}
            </span>
          </div>

          {/* Progress bar */}
          {j.status === "running" && (
            <div className="mb-2">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>{j.completed_checks} / {j.total_checks}</span>
                <span>{j.progress_pct}%</span>
              </div>
              <div className="w-full h-2 bg-dark-300 rounded-full overflow-hidden">
                <div
                  className="h-full bg-copper rounded-full transition-all"
                  style={{ width: `${j.progress_pct}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 text-xs text-gray-500">
            {j.total_checks > 0 && (
              <>
                <span>Total: {j.total_checks}</span>
                <span className="text-green-400">Done: {j.completed_checks}</span>
                {j.failed_checks > 0 && (
                  <span className="text-red-400">Failed: {j.failed_checks}</span>
                )}
              </>
            )}
            {j.next_run_at && (
              <span className="flex items-center gap-1">
                <Clock size={12} /> Next: {new Date(j.next_run_at).toLocaleDateString()}
              </span>
            )}
            {j.run_ai_analysis && (
              <span className="flex items-center gap-1 text-copper">
                <Bot size={12} /> AI Analysis
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Keywords Management Tab ─────────────────────────────────────
function KeywordsManageTab({ siteId, keywords, groups, onRefresh }) {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newKwText, setNewKwText] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [saving, setSaving] = useState(false);
  const [showModifiers, setShowModifiers] = useState(false);

  const filtered = keywords.filter((kw) => {
    if (search && !kw.keyword.toLowerCase().includes(search.toLowerCase())) return false;
    if (groupFilter && kw.group !== parseInt(groupFilter)) return false;
    return true;
  });

  const activeCount = keywords.filter((k) => k.is_active).length;
  const pausedCount = keywords.filter((k) => !k.is_active).length;

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selected.length === filtered.length) {
      setSelected([]);
    } else {
      setSelected(filtered.map((k) => k.id));
    }
  };

  const handleBulkAction = async (action, extraData = {}) => {
    if (selected.length === 0) return;
    try {
      await api.post("/rank-tracker/keywords/bulk-action/", {
        action,
        keyword_ids: selected,
        ...extraData,
      });
      setSelected([]);
      onRefresh();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.error || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this keyword?")) return;
    try {
      await api.delete(`/rank-tracker/keywords/${id}/delete/`);
      onRefresh();
    } catch {}
  };

  const handleToggleActive = async (kw) => {
    try {
      await api.patch(`/rank-tracker/keywords/${kw.id}/update/`, {
        is_active: !kw.is_active,
      });
      onRefresh();
    } catch {}
  };

  const handleAddKeywords = async () => {
    const kws = newKwText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (kws.length === 0) return;
    setSaving(true);
    try {
      await api.post("/rank-tracker/add_keywords/", {
        site_id: siteId,
        group_name: newGroupName.trim() || "",
        keywords: kws,
      });
      setNewKwText("");
      setShowAdd(false);
      onRefresh();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  const applyModifier = (mod) => {
    const lines = newKwText.split("\n").filter((s) => s.trim());
    const newLines = lines.map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return trimmed;
      return (mod.prefix || "") + trimmed + (mod.suffix || "");
    });
    setNewKwText((prev) => prev + (prev.trim() ? "\n" : "") + newLines.join("\n"));
  };

  const kwCount = newKwText.split("\n").filter((s) => s.trim()).length;

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-sm font-semibold text-white">Tracked Keywords</h3>
          <span className="text-xs text-gray-500">
            {activeCount} active{pausedCount > 0 && `, ${pausedCount} paused`}
          </span>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-copper hover:bg-copper-600 text-white rounded-lg text-xs font-medium transition"
        >
          <Plus size={14} /> Add Keywords {kwCount > 0 && `(${kwCount})`}
        </button>
      </div>

      {/* Add keywords panel */}
      {showAdd && (
        <div className="bg-dark-200 border border-copper/30 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Group Name (optional)</label>
              <input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="e.g. Services, Products..."
                className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Keyword Suggestions</label>
              <button
                onClick={() => setShowModifiers(!showModifiers)}
                className="flex items-center gap-1.5 px-3 py-2 bg-dark-300 border border-dark-50 text-gray-400 hover:text-white text-sm rounded-lg transition w-full"
              >
                <Zap size={14} className="text-copper" /> Modifier Templates
                {showModifiers ? <ChevronDown size={14} className="ml-auto" /> : <ChevronRight size={14} className="ml-auto" />}
              </button>
            </div>
          </div>

          {showModifiers && (
            <div className="flex flex-wrap gap-1.5 p-3 bg-dark-300 rounded-lg border border-dark-50">
              {KEYWORD_MODIFIERS.map((mod) => (
                <button
                  key={mod.label}
                  onClick={() => applyModifier(mod)}
                  className="px-2.5 py-1 rounded text-xs bg-dark-200 text-gray-400 hover:text-copper border border-dark-50 hover:border-copper/40 transition"
                >
                  {mod.prefix && <span className="text-copper">{mod.prefix}</span>}
                  {mod.label}
                  {mod.suffix && <span className="text-copper">{mod.suffix}</span>}
                </button>
              ))}
              <span className="text-[10px] text-gray-600 self-center ml-2">
                Click to append modified versions of existing lines
              </span>
            </div>
          )}

          <div>
            <label className="text-xs text-gray-500 mb-1 block">
              Keywords (one per line)
              <span className="ml-2 text-copper font-medium">{kwCount} keywords</span>
            </label>
            <textarea
              value={newKwText}
              onChange={(e) => setNewKwText(e.target.value)}
              rows={6}
              placeholder={"handyman services\nkitchen remodeling\nbathroom renovation\npainting contractor"}
              className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2 font-mono"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleAddKeywords}
              disabled={saving || kwCount === 0}
              className="px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
            >
              {saving ? "Adding..." : `Add ${kwCount} Keywords`}
            </button>
            <button
              onClick={() => { setShowAdd(false); setNewKwText(""); }}
              className="px-4 py-2 bg-dark-300 text-gray-400 hover:text-white border border-dark-50 rounded-lg text-sm transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters + Bulk actions */}
      <div className="flex flex-wrap items-center gap-3 bg-dark-200 border border-dark-50 rounded-xl p-3">
        <Search size={14} className="text-gray-500" />
        <input
          type="text" placeholder="Search keywords..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-1.5 w-48"
        />
        <select
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          className="bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-1.5"
        >
          <option value="">All Groups</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>

        {selected.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-copper">{selected.length} selected</span>
            <button
              onClick={() => handleBulkAction("pause")}
              className="px-2.5 py-1 rounded text-xs bg-yellow-900/30 text-yellow-300 border border-yellow-700/40 hover:bg-yellow-900/50"
            >
              <Pause size={12} className="inline mr-1" />Pause
            </button>
            <button
              onClick={() => handleBulkAction("activate")}
              className="px-2.5 py-1 rounded text-xs bg-green-900/30 text-green-300 border border-green-700/40 hover:bg-green-900/50"
            >
              <ToggleRight size={12} className="inline mr-1" />Activate
            </button>
            <button
              onClick={() => { if (window.confirm(`Delete ${selected.length} keywords?`)) handleBulkAction("delete"); }}
              className="px-2.5 py-1 rounded text-xs bg-red-900/30 text-red-300 border border-red-700/40 hover:bg-red-900/50"
            >
              <Trash2 size={12} className="inline mr-1" />Delete
            </button>
          </div>
        )}
      </div>

      {/* Keywords table */}
      <div className="bg-dark-200 border border-dark-50 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-dark-50">
              <th className="text-left px-4 py-3 w-8">
                <input
                  type="checkbox"
                  checked={selected.length === filtered.length && filtered.length > 0}
                  onChange={toggleAll}
                  className="rounded bg-dark-300 border-dark-50"
                />
              </th>
              <th className="text-left px-4 py-3">Keyword</th>
              <th className="text-left px-4 py-3">Group</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-8 text-gray-500">
                  No keywords found.
                </td>
              </tr>
            )}
            {filtered.map((kw) => (
              <tr key={kw.id} className={`border-t border-dark-50 hover:bg-dark-300/50 transition ${!kw.is_active ? "opacity-50" : ""}`}>
                <td className="px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={selected.includes(kw.id)}
                    onChange={() => toggleSelect(kw.id)}
                    className="rounded bg-dark-300 border-dark-50"
                  />
                </td>
                <td className="px-4 py-2.5 text-white font-medium">{kw.keyword}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{kw.group_name || "—"}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`px-2 py-0.5 rounded text-xs ${kw.is_active ? "bg-green-900/40 text-green-300" : "bg-gray-800 text-gray-500"}`}>
                    {kw.is_active ? "Active" : "Paused"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => handleToggleActive(kw)}
                      className="p-1.5 rounded text-gray-500 hover:text-white hover:bg-dark-300 transition"
                      title={kw.is_active ? "Pause" : "Activate"}
                    >
                      {kw.is_active ? <Pause size={14} /> : <ToggleRight size={14} />}
                    </button>
                    <button
                      onClick={() => handleDelete(kw.id)}
                      className="p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-dark-300 transition"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Locations Management Tab ────────────────────────────────────
function LocationsManageTab({ siteId, locations, onRefresh }) {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newLocText, setNewLocText] = useState("");
  const [defaultState, setDefaultState] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("United States");
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);

  const filtered = locations.filter((loc) => {
    if (search && !`${loc.city} ${loc.state} ${loc.country}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const activeCount = locations.filter((l) => l.is_active !== false).length;
  const locCount = newLocText.split("\n").filter((s) => s.trim()).length;

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selected.length === filtered.length) {
      setSelected([]);
    } else {
      setSelected(filtered.map((l) => l.id));
    }
  };

  const loadPresets = async () => {
    if (presets.length > 0) return;
    setLoadingPresets(true);
    try {
      const r = await api.get("/rank-tracker/presets/");
      setPresets(r.data.presets || []);
    } catch {}
    setLoadingPresets(false);
  };

  const applyPreset = (preset) => {
    const locs = preset.locations_json || [];
    const lines = locs.map((l) => {
      if (l.state) return `${l.city}, ${l.state}`;
      return l.city;
    });
    setNewLocText(lines.join("\n"));
    if (locs[0]?.state) setDefaultState(locs[0].state);
    if (locs[0]?.country) setDefaultCountry(locs[0].country);
  };

  const handleBulkAction = async (action) => {
    if (selected.length === 0) return;
    try {
      await api.post("/rank-tracker/locations/bulk-action/", {
        action,
        location_ids: selected,
      });
      setSelected([]);
      onRefresh();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.error || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try {
      await api.delete(`/rank-tracker/locations/${id}/delete/`);
      onRefresh();
    } catch {}
  };

  const handleToggleActive = async (loc) => {
    try {
      await api.patch(`/rank-tracker/locations/${loc.id}/update/`, {
        is_active: loc.is_active === false ? true : false,
      });
      onRefresh();
    } catch {}
  };

  const handleAddLocations = async () => {
    const lines = newLocText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (lines.length === 0) return;
    const locsData = lines.map((line) => {
      const parts = line.split(",").map((s) => s.trim());
      const city = parts[0] || "";
      let state = defaultState;
      let zipcode = "";
      if (parts[1]) {
        const stateParts = parts[1].trim().split(/\s+/);
        state = stateParts[0] || defaultState;
        zipcode = stateParts[1] || "";
      }
      return { city, state, zipcode, country: defaultCountry };
    });
    setSaving(true);
    try {
      await api.post("/rank-tracker/add_locations/", {
        site_id: siteId,
        locations: locsData,
      });
      setNewLocText("");
      setShowAdd(false);
      onRefresh();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-sm font-semibold text-white">Tracked Locations</h3>
          <span className="text-xs text-gray-500">{activeCount} active</span>
        </div>
        <button
          onClick={() => { setShowAdd(!showAdd); loadPresets(); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-copper hover:bg-copper-600 text-white rounded-lg text-xs font-medium transition"
        >
          <Plus size={14} /> Add Locations {locCount > 0 && `(${locCount})`}
        </button>
      </div>

      {/* Add locations panel */}
      {showAdd && (
        <div className="bg-dark-200 border border-copper/30 rounded-xl p-4 space-y-3">
          {/* Presets */}
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Location Presets</label>
            <div className="flex flex-wrap gap-2">
              {loadingPresets && <span className="text-xs text-gray-500">Loading...</span>}
              {presets.map((p) => (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p)}
                  className="px-2.5 py-1 rounded text-xs bg-dark-300 text-gray-400 hover:text-copper border border-dark-50 hover:border-copper/40 transition flex items-center gap-1"
                >
                  {p.is_builtin && <Globe size={10} className="text-copper" />}
                  {p.name}
                  <span className="text-gray-600">({(p.locations_json || []).length})</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Default State / Region</label>
              <input
                value={defaultState}
                onChange={(e) => setDefaultState(e.target.value)}
                placeholder="e.g. NY, AL, or leave empty"
                className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Country</label>
              <select
                value={defaultCountry}
                onChange={(e) => setDefaultCountry(e.target.value)}
                className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2"
              >
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-xs text-gray-500">
                For non-US: leave State empty, select Country
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">
              Locations (one per line: City or City, State ZIP)
              <span className="ml-2 text-copper font-medium">{locCount} locations</span>
            </label>
            <textarea
              value={newLocText}
              onChange={(e) => setNewLocText(e.target.value)}
              rows={6}
              placeholder={"Tirana\nDurres\nVlore\nSaranda\nBerat"}
              className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2 font-mono"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleAddLocations}
              disabled={saving || locCount === 0}
              className="px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
            >
              {saving ? "Adding..." : `Add ${locCount} Locations`}
            </button>
            <button
              onClick={() => { setShowAdd(false); setNewLocText(""); }}
              className="px-4 py-2 bg-dark-300 text-gray-400 hover:text-white border border-dark-50 rounded-lg text-sm transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters + Bulk actions */}
      <div className="flex flex-wrap items-center gap-3 bg-dark-200 border border-dark-50 rounded-xl p-3">
        <Search size={14} className="text-gray-500" />
        <input
          type="text" placeholder="Search locations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-1.5 w-48"
        />

        {selected.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-copper">{selected.length} selected</span>
            <button
              onClick={() => handleBulkAction("pause")}
              className="px-2.5 py-1 rounded text-xs bg-yellow-900/30 text-yellow-300 border border-yellow-700/40 hover:bg-yellow-900/50"
            >
              <Pause size={12} className="inline mr-1" />Pause
            </button>
            <button
              onClick={() => handleBulkAction("activate")}
              className="px-2.5 py-1 rounded text-xs bg-green-900/30 text-green-300 border border-green-700/40 hover:bg-green-900/50"
            >
              <ToggleRight size={12} className="inline mr-1" />Activate
            </button>
            <button
              onClick={() => { if (window.confirm(`Delete ${selected.length} locations?`)) handleBulkAction("delete"); }}
              className="px-2.5 py-1 rounded text-xs bg-red-900/30 text-red-300 border border-red-700/40 hover:bg-red-900/50"
            >
              <Trash2 size={12} className="inline mr-1" />Delete
            </button>
          </div>
        )}
      </div>

      {/* Locations table */}
      <div className="bg-dark-200 border border-dark-50 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-dark-50">
              <th className="text-left px-4 py-3 w-8">
                <input
                  type="checkbox"
                  checked={selected.length === filtered.length && filtered.length > 0}
                  onChange={toggleAll}
                  className="rounded bg-dark-300 border-dark-50"
                />
              </th>
              <th className="text-left px-4 py-3">City</th>
              <th className="text-left px-4 py-3">State</th>
              <th className="text-left px-4 py-3">Country</th>
              <th className="text-left px-4 py-3">Zipcode</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-500">
                  No locations found.
                </td>
              </tr>
            )}
            {filtered.map((loc) => (
              <tr key={loc.id} className={`border-t border-dark-50 hover:bg-dark-300/50 transition ${loc.is_active === false ? "opacity-50" : ""}`}>
                <td className="px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={selected.includes(loc.id)}
                    onChange={() => toggleSelect(loc.id)}
                    className="rounded bg-dark-300 border-dark-50"
                  />
                </td>
                <td className="px-4 py-2.5 text-white font-medium">{loc.city}</td>
                <td className="px-4 py-2.5 text-gray-400">{loc.state || "—"}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{loc.country || "United States"}</td>
                <td className="px-4 py-2.5 text-gray-500 text-xs">{loc.zipcode || "—"}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`px-2 py-0.5 rounded text-xs ${loc.is_active !== false ? "bg-green-900/40 text-green-300" : "bg-gray-800 text-gray-500"}`}>
                    {loc.is_active !== false ? "Active" : "Paused"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => handleToggleActive(loc)}
                      className="p-1.5 rounded text-gray-500 hover:text-white hover:bg-dark-300 transition"
                      title={loc.is_active !== false ? "Pause" : "Activate"}
                    >
                      {loc.is_active !== false ? <Pause size={14} /> : <ToggleRight size={14} />}
                    </button>
                    <button
                      onClick={() => handleDelete(loc.id)}
                      className="p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-dark-300 transition"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Job Creation Modal ──────────────────────────────────────────
function JobModal({
  groups, locations, apiUsage,
  jobName, setJobName, jobFreq, setJobFreq,
  jobAI, setJobAI, jobGroupIds, setJobGroupIds,
  jobLocationIds, setJobLocationIds,
  onCreate, onClose,
}) {
  const [locSearch, setLocSearch] = useState("");

  const toggleGroup = (id) => {
    setJobGroupIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleLocation = (id) => {
    setJobLocationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectTopCities = (n) => {
    const topCities = ["Yonkers", "White Plains", "New Rochelle", "Mount Vernon",
      "Tarrytown", "Scarsdale", "Rye", "Bronxville", "Peekskill", "Ossining",
      "Mamaroneck", "Larchmont", "Harrison", "Port Chester", "Dobbs Ferry"];
    const ids = locations
      .filter((l) => topCities.slice(0, n).includes(l.city))
      .map((l) => l.id);
    setJobLocationIds(ids);
  };

  // Estimate counts
  const selectedKeywords = jobGroupIds.length > 0
    ? groups.filter((g) => jobGroupIds.includes(g.id)).reduce((sum, g) => sum + g.keyword_count, 0)
    : groups.reduce((sum, g) => sum + g.keyword_count, 0);
  const selectedLocations = jobLocationIds.length > 0
    ? jobLocationIds.length
    : locations.length;
  const totalChecks = selectedKeywords * selectedLocations;
  const estMinutes = Math.ceil((totalChecks * 2.5) / 60);

  // Budget warning
  const remaining = apiUsage?.calls_remaining ?? Infinity;
  const overBudget = totalChecks > remaining;

  const filteredLocations = locSearch
    ? locations.filter((l) => l.city.toLowerCase().includes(locSearch.toLowerCase()))
    : locations;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-200 border border-dark-50 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-bold text-white">New Rank Check</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Job Name</label>
              <input
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
                placeholder={`Rank Check ${new Date().toLocaleDateString()}`}
                className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Frequency</label>
              <select
                value={jobFreq}
                onChange={(e) => setJobFreq(e.target.value)}
                className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2"
              >
                <option value="manual">Manual (one-time)</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {/* Keyword group filter */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-500">
                Keyword Groups
                <span className="text-gray-600 ml-1">
                  ({jobGroupIds.length === 0 ? `all ${groups.reduce((s, g) => s + g.keyword_count, 0)}` : `${selectedKeywords}`} keywords)
                </span>
              </label>
              {jobGroupIds.length > 0 && (
                <button onClick={() => setJobGroupIds([])} className="text-xs text-gray-600 hover:text-gray-400">
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {groups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => toggleGroup(g.id)}
                  className={`px-2.5 py-1 rounded text-xs border transition ${
                    jobGroupIds.includes(g.id)
                      ? "bg-copper/20 text-copper border-copper/40"
                      : "bg-dark-300 text-gray-400 border-dark-50 hover:text-white"
                  }`}
                >
                  {g.name} ({g.keyword_count})
                </button>
              ))}
            </div>
          </div>

          {/* Location filter */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-500">
                Locations
                <span className="text-gray-600 ml-1">
                  ({jobLocationIds.length === 0 ? `all ${locations.length}` : jobLocationIds.length} cities)
                </span>
              </label>
              <div className="flex items-center gap-2">
                <button onClick={() => selectTopCities(3)} className="text-xs text-copper hover:text-copper-400">
                  Top 3
                </button>
                <button onClick={() => selectTopCities(5)} className="text-xs text-copper hover:text-copper-400">
                  Top 5
                </button>
                <button onClick={() => selectTopCities(10)} className="text-xs text-copper hover:text-copper-400">
                  Top 10
                </button>
                <button onClick={() => selectTopCities(15)} className="text-xs text-copper hover:text-copper-400">
                  Top 15
                </button>
                {jobLocationIds.length > 0 && (
                  <button onClick={() => setJobLocationIds([])} className="text-xs text-gray-600 hover:text-gray-400">
                    Clear
                  </button>
                )}
              </div>
            </div>
            <input
              type="text" placeholder="Search cities..."
              value={locSearch}
              onChange={(e) => setLocSearch(e.target.value)}
              className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-1.5 mb-2"
            />
            <div className="max-h-40 overflow-y-auto bg-dark-300 border border-dark-50 rounded-lg p-2 grid grid-cols-2 sm:grid-cols-3 gap-1">
              {filteredLocations.map((l) => (
                <label
                  key={l.id}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer transition ${
                    jobLocationIds.includes(l.id)
                      ? "bg-copper/15 text-copper"
                      : "text-gray-400 hover:text-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={jobLocationIds.includes(l.id)}
                    onChange={() => toggleLocation(l.id)}
                    className="rounded bg-dark-200 border-dark-50 w-3 h-3"
                  />
                  {l.city}
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox" checked={jobAI}
              onChange={(e) => setJobAI(e.target.checked)}
              className="rounded bg-dark-300 border-dark-50"
            />
            Run AI analysis after completion
          </label>

          {/* Credit impact preview */}
          <div className={`rounded-lg p-3 flex items-center justify-between border ${
            overBudget
              ? "bg-red-900/20 border-red-700/40"
              : "bg-dark-300 border-dark-50"
          }`}>
            <div className="text-xs text-gray-400">
              <span className="text-white font-bold">{selectedKeywords}</span> keywords
              <span className="text-gray-600 mx-1">&times;</span>
              <span className="text-white font-bold">{selectedLocations}</span> locations
              <span className="text-gray-600 mx-1">=</span>
              <span className={`font-bold ${overBudget ? "text-red-400" : "text-copper"}`}>{totalChecks}</span> API calls
            </div>
            <div className="text-xs text-right">
              <div className="text-gray-500">~{estMinutes} min</div>
              {apiUsage && (
                <div className={overBudget ? "text-red-400" : "text-gray-600"}>
                  {overBudget ? (
                    <span className="flex items-center gap-1">
                      <AlertTriangle size={10} /> Exceeds budget by {totalChecks - remaining}
                    </span>
                  ) : (
                    `${remaining - totalChecks} credits remaining after`
                  )}
                </div>
              )}
            </div>
          </div>

          {overBudget && (
            <div className="flex items-center gap-2 p-2 bg-red-900/20 border border-red-700/40 rounded-lg text-xs text-red-300">
              <AlertTriangle size={14} />
              This job will exceed your monthly API budget. Consider reducing keywords or locations.
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={onCreate}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                overBudget
                  ? "bg-red-900/40 text-red-300 border border-red-700/40 hover:bg-red-900/60"
                  : "bg-copper hover:bg-copper-600 text-white"
              }`}
            >
              {overBudget ? `Start Anyway (${totalChecks} checks)` : `Start Rank Check (${totalChecks} checks)`}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 bg-dark-300 text-gray-400 hover:text-white border border-dark-50 rounded-lg text-sm transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Setup Panel (add keywords + locations for any site) ─────────
function SetupPanel({ siteId, domain, onComplete }) {
  const [groupName, setGroupName] = useState("");
  const [kwText, setKwText] = useState("");
  const [locText, setLocText] = useState("");
  const [defaultState, setDefaultState] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("United States");
  const [saving, setSaving] = useState(false);
  const [addedKw, setAddedKw] = useState(0);
  const [addedLoc, setAddedLoc] = useState(0);
  const [presets, setPresets] = useState([]);
  const [showModifiers, setShowModifiers] = useState(false);

  const isGcmm = domain && (domain.includes("gcmm") || domain.includes("gcmmdentalconstruction"));

  useEffect(() => {
    api.get("/rank-tracker/presets/").then((r) => setPresets(r.data.presets || [])).catch(() => {});
  }, []);

  const handleSeedGcmm = async () => {
    setSaving(true);
    try {
      await api.post("/rank-tracker/seed/", { site_id: siteId });
      setTimeout(() => { setSaving(false); onComplete(); }, 3000);
    } catch {
      setSaving(false);
    }
  };

  const handleAddKeywords = async () => {
    const keywords = kwText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (keywords.length === 0) return;
    setSaving(true);
    try {
      const r = await api.post("/rank-tracker/add_keywords/", {
        site_id: siteId,
        group_name: groupName.trim() || "",
        keywords,
      });
      setAddedKw((p) => p + (r.data.created || 0));
      setKwText("");
      onComplete();
    } catch {}
    setSaving(false);
  };

  const handleAddLocations = async () => {
    const lines = locText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (lines.length === 0) return;
    const locations = lines.map((line) => {
      const parts = line.split(",").map((s) => s.trim());
      const city = parts[0] || "";
      let state = defaultState;
      let zipcode = "";
      if (parts[1]) {
        const stateParts = parts[1].trim().split(/\s+/);
        state = stateParts[0] || defaultState;
        zipcode = stateParts[1] || "";
      }
      return { city, state, zipcode, country: defaultCountry };
    });
    setSaving(true);
    try {
      const r = await api.post("/rank-tracker/add_locations/", {
        site_id: siteId,
        locations,
      });
      setAddedLoc((p) => p + (r.data.created || 0));
      setLocText("");
      onComplete();
    } catch {}
    setSaving(false);
  };

  const applyPreset = (preset) => {
    const locs = preset.locations_json || [];
    const lines = locs.map((l) => {
      if (l.state) return `${l.city}, ${l.state}`;
      return l.city;
    });
    setLocText(lines.join("\n"));
    if (locs[0]?.state) setDefaultState(locs[0].state);
    if (locs[0]?.country) setDefaultCountry(locs[0].country);
  };

  const applyModifier = (mod) => {
    const lines = kwText.split("\n").filter((s) => s.trim());
    const newLines = lines.map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return trimmed;
      return (mod.prefix || "") + trimmed + (mod.suffix || "");
    });
    setKwText((prev) => prev + (prev.trim() ? "\n" : "") + newLines.join("\n"));
  };

  const kwCount = kwText.split("\n").filter((s) => s.trim()).length;
  const locCount = locText.split("\n").filter((s) => s.trim()).length;

  return (
    <div className="space-y-4">
      <div className="bg-dark-200 border border-copper/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Target size={24} className="text-copper" />
          <h2 className="text-lg font-bold text-white">
            Set Up Rank Tracking for {domain}
          </h2>
        </div>

        {isGcmm && (
          <div className="mb-4 p-3 bg-dark-300 rounded-lg flex items-center justify-between">
            <span className="text-sm text-gray-300">
              GCMM site detected — seed with Westchester County keywords &amp; locations?
            </span>
            <button
              onClick={handleSeedGcmm}
              disabled={saving}
              className="px-4 py-1.5 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
            >
              {saving ? "Seeding..." : "Seed GCMM Defaults"}
            </button>
          </div>
        )}

        {(addedKw > 0 || addedLoc > 0) && (
          <div className="mb-4 p-3 bg-green-900/20 border border-green-700/30 rounded-lg text-sm text-green-300">
            Added {addedKw} keywords and {addedLoc} locations. You can add more or click "Run Rank Check" above when ready.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Keywords */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              Add Keywords
              {kwCount > 0 && <span className="text-copper text-xs font-normal">({kwCount} keywords)</span>}
            </h3>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Group Name (optional)</label>
              <input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g. Tourism, Hotels, Restaurants..."
                className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2"
              />
            </div>

            {/* Keyword Suggestion Button */}
            <div>
              <button
                onClick={() => setShowModifiers(!showModifiers)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-300 border border-dark-50 text-gray-400 hover:text-copper text-xs rounded-lg transition"
              >
                <Zap size={12} className="text-copper" /> Modifier Templates
                {showModifiers ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
              {showModifiers && (
                <div className="mt-2 flex flex-wrap gap-1.5 p-2 bg-dark-300 rounded-lg border border-dark-50">
                  {KEYWORD_MODIFIERS.map((mod) => (
                    <button
                      key={mod.label}
                      onClick={() => applyModifier(mod)}
                      className="px-2 py-0.5 rounded text-xs bg-dark-200 text-gray-400 hover:text-copper border border-dark-50 hover:border-copper/40 transition"
                    >
                      {mod.prefix && <span className="text-copper">{mod.prefix}</span>}
                      {mod.label}
                      {mod.suffix && <span className="text-copper">{mod.suffix}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Keywords (one per line)</label>
              <textarea
                value={kwText}
                onChange={(e) => setKwText(e.target.value)}
                rows={8}
                placeholder={"best restaurants tirana\nthings to do in albania\nalbanian riviera hotels\nberat castle tours\ntirana nightlife"}
                className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2 font-mono"
              />
            </div>
            <button
              onClick={handleAddKeywords}
              disabled={saving || !kwText.trim()}
              className="w-full px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
            >
              {saving ? "Adding..." : `Add Keywords (${kwCount})`}
            </button>
          </div>

          {/* Locations */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              Add Locations
              {locCount > 0 && <span className="text-copper text-xs font-normal">({locCount} locations)</span>}
            </h3>

            {/* Location Presets */}
            {presets.length > 0 && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Location Presets</label>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => applyPreset(p)}
                      className="px-2 py-1 rounded text-xs bg-dark-300 text-gray-400 hover:text-copper border border-dark-50 hover:border-copper/40 transition flex items-center gap-1"
                    >
                      {p.is_builtin && <Globe size={10} className="text-copper" />}
                      {p.name} ({(p.locations_json || []).length})
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Default State / Region</label>
                <input
                  value={defaultState}
                  onChange={(e) => setDefaultState(e.target.value)}
                  placeholder="e.g. NY, AL, or leave empty"
                  className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Country</label>
                <select
                  value={defaultCountry}
                  onChange={(e) => setDefaultCountry(e.target.value)}
                  className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                Locations (one per line: City or City, State ZIP)
              </label>
              <textarea
                value={locText}
                onChange={(e) => setLocText(e.target.value)}
                rows={8}
                placeholder={"Tirana\nDurres\nVlore\nSaranda\nBerat\nGjirokaster\nShengjin\nKsamil\nKorce\nPogradec"}
                className="w-full bg-dark-300 border border-dark-50 text-white text-sm rounded-lg px-3 py-2 font-mono"
              />
            </div>
            <button
              onClick={handleAddLocations}
              disabled={saving || !locText.trim()}
              className="w-full px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
            >
              {saving ? "Adding..." : `Add Locations (${locCount})`}
            </button>
          </div>
        </div>

        {/* Credit preview in setup */}
        {kwCount > 0 && locCount > 0 && (
          <div className="mt-4 p-3 bg-dark-300 border border-dark-50 rounded-lg flex items-center justify-between">
            <div className="text-xs text-gray-400">
              <Zap size={12} className="inline text-copper mr-1" />
              Credit Impact Preview:
              <span className="text-white font-bold ml-1">{kwCount}</span> keywords
              <span className="text-gray-600 mx-1">&times;</span>
              <span className="text-white font-bold">{locCount}</span> locations
              <span className="text-gray-600 mx-1">=</span>
              <span className="text-copper font-bold">{kwCount * locCount}</span> API calls per rank check
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
